import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LastMinuteInviteManager.css';

const LastMinuteInviteManager = ({ tournamentId, tournamentName }) => {
  const [spots, setSpots] = useState([]);
  const [pendingReservations, setPendingReservations] = useState({});
  const [newSpots, setNewSpots] = useState([{
    teeTime: '8:30 AM',
    groupNumber: 1,
    position: 1
  }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showInviteWizard, setShowInviteWizard] = useState(false);
  const [optedInMembers, setOptedInMembers] = useState([]);

  const { authenticatedFetch } = useAuth();

  useEffect(() => {
    loadData();
  }, [tournamentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load available spots
      const spotsResponse = await authenticatedFetch(`/api/tournaments/${tournamentId}/last-minute-spots`);
      const spotsData = await spotsResponse.json();
      
      // Load pending reservations
      const reservationsResponse = await authenticatedFetch(`/api/tournaments/${tournamentId}/last-minute-reservations`);
      const reservationsData = await reservationsResponse.json();
      
      if (spotsData.success) {
        setSpots(spotsData.data.spots);
      }
      
      if (reservationsData.success) {
        setPendingReservations(reservationsData.data.spotGroups);
      }
      
    } catch (err) {
      console.error('Error loading last minute data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadOptedInMembers = async () => {
    try {
      // Get all members who have opted in for last-minute invites
      const response = await authenticatedFetch('/api/members?status=active&limit=1000');
      const data = await response.json();
      
      if (data.success) {
        const optedIn = data.data.members.filter(member => 
          member.lastMinuteInvitePreferences?.acceptInvites === true
        );
        setOptedInMembers(optedIn);
      }
    } catch (err) {
      console.error('Error loading opted-in members:', err);
    }
  };

  const showInvitePreview = async () => {
    await loadOptedInMembers();
    setShowInviteWizard(true);
  };

  const createSpots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch(`/api/tournaments/${tournamentId}/last-minute-spots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          spots: newSpots
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`✅ Created ${data.data.spots.length} last-minute invite spots!`);
        setNewSpots([{ teeTime: '8:30 AM', groupNumber: 1, position: 1 }]); // Reset form
        loadData(); // Refresh data
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to create spots');
      }
    } catch (err) {
      console.error('Error creating spots:', err);
      setError(err.message || 'Failed to create spots');
    } finally {
      setLoading(false);
    }
  };

  const createSpotsAndSendInvites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First create the spots
      await createSpots();
      
      // Then show the invite wizard
      if (!error) {
        await showInvitePreview();
      }
    } catch (err) {
      console.error('Error in create and invite flow:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNewSpotRow = () => {
    setNewSpots(prev => [...prev, {
      teeTime: '8:30 AM',
      groupNumber: 1,
      position: 1
    }]);
  };

  const updateSpotRow = (index, field, value) => {
    setNewSpots(prev => prev.map((spot, i) => 
      i === index ? { ...spot, [field]: value } : spot
    ));
  };

  const removeSpotRow = (index) => {
    if (newSpots.length > 1) {
      setNewSpots(prev => prev.filter((_, i) => i !== index));
    }
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 7; hour <= 16; hour++) {
      for (let min of [0, 10, 20, 30, 40, 50]) {
        const time12 = new Date(2025, 0, 1, hour, min).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        times.push(time12);
      }
    }
    return times;
  };

  if (loading && spots.length === 0) {
    return (
      <div className="invite-manager loading">
        <div className="loading-spinner"></div>
        <p>Loading last-minute invite data...</p>
      </div>
    );
  }

  return (
    <div className="invite-manager">
      <div className="manager-header">
        <h2>🕐 Last-Minute Invite Management</h2>
        <p>Create and manage last-minute spots for <strong>{tournamentName}</strong></p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Create New Spots Section */}
      <div className="create-spots-section">
        <h3>➕ Create Available Spots</h3>
        <p>Add empty spots that members can request through last-minute invites</p>
        
        <div className="new-spots-form">
          {newSpots.map((spot, index) => (
            <div key={index} className="spot-row">
              <div className="spot-inputs">
                <div className="input-group">
                  <label>Tee Time</label>
                  <select
                    value={spot.teeTime}
                    onChange={(e) => updateSpotRow(index, 'teeTime', e.target.value)}
                  >
                    {generateTimeOptions().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                
                <div className="input-group">
                  <label>Group #</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={spot.groupNumber}
                    onChange={(e) => updateSpotRow(index, 'groupNumber', parseInt(e.target.value) || 1)}
                  />
                </div>
                
                <div className="input-group">
                  <label>Position</label>
                  <select
                    value={spot.position}
                    onChange={(e) => updateSpotRow(index, 'position', parseInt(e.target.value) || 1)}
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                  </select>
                </div>
                
                <div className="input-actions">
                  {newSpots.length > 1 && (
                    <button
                      onClick={() => removeSpotRow(index)}
                      className="remove-button"
                      title="Remove this spot"
                    >
                      ❌
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <div className="form-actions">
            <button onClick={addNewSpotRow} className="add-spot-button">
              ➕ Add Another Spot
            </button>
            
            <button 
              onClick={createSpots}
              disabled={loading || newSpots.length === 0}
              className="create-spots-button"
            >
              {loading ? 'Creating...' : `Create ${newSpots.length} Spot${newSpots.length !== 1 ? 's' : ''}`}
            </button>
            
            <button 
              onClick={createSpotsAndSendInvites}
              disabled={loading || newSpots.length === 0}
              className="create-and-invite-button"
            >
              {loading ? 'Processing...' : `📧 Create & Send Invites`}
            </button>
          </div>
        </div>
      </div>

      {/* Current Available Spots */}
      <div className="current-spots-section">
        <h3>📋 Current Available Spots ({spots.length})</h3>
        
        {spots.length === 0 ? (
          <div className="empty-spots">
            <p>No last-minute spots created yet. Create some spots above to get started!</p>
          </div>
        ) : (
          <div className="spots-list">
            {spots.map((spot) => (
              <div key={spot._id} className="current-spot-card">
                <div className="spot-info">
                  <span className="spot-time">{spot.teeTime}</span>
                  <span className="spot-details">Group {spot.groupNumber}, Position {spot.position}</span>
                  <span className="spot-requests">
                    {spot.requestCount === 0 ? '📧 No requests yet' : 
                     spot.requestCount === 1 ? '🔥 1 request' : 
                     `🔥 ${spot.requestCount} requests`}
                  </span>
                </div>
                
                <div className="spot-actions">
                  <button className="view-details-button">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Reservations Management */}
      <div className="reservations-section">
        <h3>⏳ Pending Reservations ({Object.keys(pendingReservations).length})</h3>
        
        {Object.keys(pendingReservations).length === 0 ? (
          <div className="empty-reservations">
            <p>No pending reservations. Members haven't requested any spots yet.</p>
          </div>
        ) : (
          <div className="reservations-list">
            {Object.entries(pendingReservations).map(([spotId, group]) => (
              <div key={spotId} className="reservation-group">
                <div className="reservation-header">
                  <h4>{group.spot.teeTime} Group {group.spot.groupNumber} Position {group.spot.position}</h4>
                  {group.reservations.length > 1 && (
                    <span className="conflict-badge">⚠️ CONFLICT ({group.reservations.length} requests)</span>
                  )}
                </div>
                
                <div className="reservations">
                  {group.reservations.map((reservation, index) => (
                    <div key={reservation._id} className="reservation-card">
                      <div className="reservation-info">
                        <div className="member-info">
                          <strong>{reservation.member.firstName} {reservation.member.lastName}</strong>
                          <span className="member-details">
                            GHIN: {reservation.member.ghin} | HI: {reservation.member.handicapIndex}
                          </span>
                          <span className="request-time">
                            Requested: {new Date(reservation.reservedAt).toLocaleString()}
                          </span>
                        </div>
                        
                        {group.reservations.length > 1 && (
                          <div className="priority-badge">
                            #{index + 1}
                          </div>
                        )}
                      </div>
                      
                      <div className="reservation-actions">
                        <button className="approve-button">
                          ✅ Place
                        </button>
                        <button className="revoke-button">
                          ❌ Decline
                        </button>
                      </div>
                      
                      {reservation.specialRequests && (
                        <div className="special-requests">
                          <strong>Notes:</strong> {reservation.specialRequests}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Preview Wizard */}
      {showInviteWizard && (
        <div className="invite-wizard-overlay">
          <div className="invite-wizard">
            <div className="wizard-header">
              <h3>📧 Send Last-Minute Invites</h3>
              <button 
                onClick={() => setShowInviteWizard(false)}
                className="close-wizard"
              >
                ✕
              </button>
            </div>
            
            <div className="wizard-content">
              <div className="invite-summary">
                <h4>📍 Available Spots ({newSpots.length})</h4>
                <div className="spots-preview">
                  {newSpots.map((spot, index) => (
                    <div key={index} className="spot-preview">
                      {spot.teeTime} - Group {spot.groupNumber}, Position {spot.position}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="member-preview">
                <h4>👥 Members Who Will Be Notified ({optedInMembers.length})</h4>
                {optedInMembers.length === 0 ? (
                  <div className="no-opted-in">
                    <p>⚠️ No members have opted in for last-minute invites.</p>
                    <p>Members need to enable this preference in their profile settings.</p>
                  </div>
                ) : (
                  <div className="members-list">
                    {optedInMembers.slice(0, 10).map(member => (
                      <div key={member._id} className="member-preview">
                        <span className="member-name">{member.firstName} {member.lastName}</span>
                        <span className="member-details">
                          GHIN: {member.ghin} | HI: {member.handicapIndex}
                        </span>
                        <span className="member-email">{member.email}</span>
                      </div>
                    ))}
                    {optedInMembers.length > 10 && (
                      <div className="more-members">
                        + {optedInMembers.length - 10} more members
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="wizard-actions">
              <button 
                onClick={() => setShowInviteWizard(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  // TODO: Implement actual email sending
                  setSuccess('📧 Invites sent to ' + optedInMembers.length + ' members!');
                  setShowInviteWizard(false);
                  setTimeout(() => setSuccess(null), 5000);
                }}
                disabled={optedInMembers.length === 0}
                className="send-invites-button"
              >
                📧 Send Invites to {optedInMembers.length} Members
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LastMinuteInviteManager;
